import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { removeQueryParams } from 'Utils/removeQueryParams';
import { useSessionStorage } from 'Hooks/useStorage';

import { createErrorLog } from './utils';

/**
 * Custom hook that handles errors related to session/auth0
 */
export const useSessionErrors = () => {
  const {
    error: authError,
    user,
    logout,
    isAuthenticated,
  } = useAuth0();

  const [errorLoops, setErrorLoops] = useSessionStorage('auth_error_loops', 0);
  const resetErrorLoops = () => setErrorLoops(0);

  // Reset loop counter after 30 seconds
  useEffect(() => {
    if (isAuthenticated) {
      const timeoutId = setTimeout(() => {
        resetErrorLoops();
      }, 30000);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [isAuthenticated]);

  const userId = user?.sub?.replace('auth0|', '');
  const email = user?.email;

  const handleSessionError = (error: Error | undefined) => {
    if (!error) return;

    if (errorLoops >= 5) {
      createErrorLog('Session error with too many retries', {
        email,
        errorLoops,
        isAuthenticated,
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
        userId,
      });
      resetErrorLoops();
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}/pay/error/?reason=too_many_retries`,
        },
      });
      return;
    }
    const isExpTime = error?.message?.includes('Expiration Time');
    const isInvalidState = error?.message?.includes('Invalid state');

    createErrorLog('Session auth0 error', {
      email,
      isAuthenticated,
      isExpTime,
      isInvalidState,
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      userId,
    });

    if (isExpTime) {
      // Log out the user and notify them about the error related to device time mismatch.
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}/pay/error/?reason=exp_time`,
        },
      });
    }

    if (isInvalidState) {
      // Remove the `code` and `state` query params to prevent loops
      removeQueryParams(['code', 'state']);
    }

    setErrorLoops(errorLoops + 1);
  };

  useEffect(() => {
    handleSessionError(authError);
  }, [authError, isAuthenticated, userId, email]);
};
